import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function ResetPassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('form'); // form | success | error
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const tokenRef = useRef('');
  
  useEffect(() => {
    // Extract token from hash
    const hash = window.location.hash;
    const match = hash.match(/reset_password=([^&]+)/);
    
    if (match) {
      tokenRef.current = match[1];
    } else {
      setStatus('error');
      setErrorMessage('Lien invalide ou expiré');
    }
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: tokenRef.current,
          new_password: password
        })
      });
      
      if (response.ok) {
        setStatus('success');
      } else {
        const err = await response.json();
        setStatus('error');
        setErrorMessage(err.detail || 'Erreur lors de la réinitialisation');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const handleContinue = () => {
    // Clear hash and redirect
    window.history.replaceState(null, '', window.location.pathname);
    navigate('/home');
  };
  
  return (
    <div className="h-full w-full flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-light rounded-2xl p-6 text-center max-w-sm w-full"
      >
        {status === 'form' && (
          <>
            <div className="w-16 h-16 bg-secondary/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Lock size={32} className="text-secondary" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Nouveau mot de passe</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Choisissez un nouveau mot de passe sécurisé.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Nouveau mot de passe</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-10"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Confirmer le mot de passe</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pl-10"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
              </button>
            </form>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Mot de passe réinitialisé !</h2>
            <p className="text-muted-foreground mb-6">
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <button
              onClick={handleContinue}
              className="w-full btn-primary"
              data-testid="continue-btn"
            >
              Se connecter
            </button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <XCircle size={40} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Erreur</h2>
            <p className="text-muted-foreground mb-6">{errorMessage}</p>
            <button
              onClick={handleContinue}
              className="w-full btn-secondary"
              data-testid="back-btn"
            >
              Retour à l'accueil
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
